import { useCallback, useState } from "react"

import { AgencyClientProduct, StartWeekDay } from "models/agency"
import { CellProps, Column } from "react-table"
import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { defaultSort, limitAndOffset } from "api/utils"
import { fetchProducts, handleDeleteProduct } from "api/products"
import { fetchAgencyClientProfile, fetchClientConfigProfile } from "api/clients"
import { queryCache, useMutation, useQuery } from "react-query"

import { Box } from "@material-ui/core"
import Button from "ui/buttons/Button"
import CreateProduct from "../../MarketClients/Details/CreateProducts"
import { ReactComponent as DeleteIcon } from "assets/delete.svg"
import { ReactComponent as EditIcon } from "assets/icon-edit.svg"
import EditProduct from "../../MarketClients/Details/EditProducts"
import { EmptyState } from "components/EmptyState"
import MoreActions from "components/MoreActions"
import { ReactComponent as ProductIcon } from "assets/icon-product.svg"
import { Table } from "components/tables"
import { css } from "@emotion/css"
import { useAuthenticated } from "providers/A12n"
import { useFeedback } from "components/FeedbackSnackbar"
import { useI18n } from "providers/I18n"
import { useLocationSearch } from "hooks/useLocationSearch"
import { useParams } from "react-router-dom"
import { useToggle } from "hooks/useToggle"
import { useAgencyId } from "hooks/useAgencyId"

const styles = {
  wrapper: css`
    padding: var(--spacing-xxl-2) 0 0;
  `,
}

const DYNAMIC_START_OF_WEEK_OPTIONS: StartWeekDay[] = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
]

const COLUMNS = (
  t: (k: string) => string,
  onDelete: (id: string) => void,
  onEdit: (product: AgencyClientProduct) => void,
  productCudEnabled: boolean | undefined,
): Array<
  Column<AgencyClientProduct> & {
    header: string
    fixed?: boolean
    variant?: string
  }
> => [
  {
    header: "agency.client.product.name",
    id: "name",
    accessor: "name",
  },
  {
    header: "agency.client.product.division",
    id: "division",
    accessor: "division",
  },
  {
    header: "agency.client.product.code",
    id: "code",
    accessor: "code",
  },
  {
    header: "agency.client.product.startWeekDay",
    id: "startWeekDay",
    accessor: "startWeekDay",
  },
  {
    header: "product.actions",
    id: "actions",
    align: "right",
    Cell: function (props: CellProps<AgencyClientProduct>) {
      const { id } = props.row.original
      return (
        <MoreActions
          options={[
            // DELETE
            {
              label: t("product.delete.action"),
              icon: <DeleteIcon />,
              disabled: !productCudEnabled,
              onClick: () => {
                onDelete(id)
              },
            },
            // EDIT
            {
              label: t("product.edit.action"),
              icon: <EditIcon />,
              disabled: !productCudEnabled,
              onClick: () => {
                onEdit(props.row.original)
              },
            },
          ]}
        />
      )
    },
  },
]

export const SORT_BY_DEFAULT = "name,ASC"

export function AgencyClientProducts() {
  const { t } = useI18n()
  const { id, configId } = useParams<{ id: string; configId: string }>()
  const ls = useLocationSearch()
  const [modalInfo, setModalInfo] = useState<ModalInfo>()
  const sendFeedback = useFeedback((s) => s.send)
  const [product, setProduct] = useState<AgencyClientProduct>()
  const { open, onOpen, onClose } = useToggle()
  const { meta } = useAuthenticated()
  const isTeamLead = meta?.teamLead
  const { agencyId } = useAgencyId()

  const params = ls.map(limitAndOffset).map(defaultSort(SORT_BY_DEFAULT))
  params.set("clientId", id)

  const { data, status } = useQuery(
    ["client-agency-products", params.toString()],
    fetchProducts,
  )

  const queryFn = isTeamLead
    ? fetchClientConfigProfile
    : fetchAgencyClientProfile
  const queryId = isTeamLead ? configId : id
  const enabled = isTeamLead ? queryId : agencyId

  const profileData = useQuery(["agency-client", queryId, agencyId], queryFn, {
    enabled,
    onError: (e) => sendFeedback(t(e.message), "error"),
  })

  const [deleteProduct] = useMutation(handleDeleteProduct, {
    onSuccess: () => {
      queryCache.removeQueries("client-agency-products")
      setModalInfo(undefined)
    },
    onError: (e) => {
      setModalInfo(undefined)
      sendFeedback(
        t(e.message) || t("agency-products.list.delete.error"),
        "error",
      )
    },
  })

  const onDelete = useCallback(
    (id: string) => {
      setModalInfo({
        title: t("agency-products.list.delete.title"),
        type: "primary_destructive",
        description: t("agency-products.list.delete.description"),
        submitLabel: t("agency-products.list.delete.action"),
        onSubmit: async () => await deleteProduct(id),
      })
    },
    [deleteProduct, t],
  )

  const onEdit = useCallback(
    (product: AgencyClientProduct) => setProduct(product),
    [],
  )

  const onEditClose = useCallback(() => setProduct(undefined), [])

  const products = data?.data

  const isModalOpen = Boolean(modalInfo)

  return (
    <Box className={styles.wrapper}>
      <Table<AgencyClientProduct>
        id="products-list-table"
        columns={COLUMNS(
          t,
          onDelete,
          onEdit,
          profileData?.data?.productCudEnabled,
        )}
        data={products ?? []}
        filters={[]}
        total={data?.total ?? 0}
        loading={status === "loading"}
        searchBy="search"
        hasColumnsReordering={false}
        noResults={
          <EmptyState
            title={t("agency.client.products.noresults.title")}
            icon={<ProductIcon />}
          />
        }
        actionButton={
          <>
            <Button
              variant="contained"
              onClick={onOpen}
              color="primary"
              disabled={!profileData?.data?.productCudEnabled}
            >
              {t("products.create.button")}
            </Button>
            <CreateProduct
              open={open}
              onClose={onClose}
              options={DYNAMIC_START_OF_WEEK_OPTIONS}
            />
          </>
        }
      />
      <ConfirmationModal
        open={isModalOpen}
        onCancel={() => setModalInfo(undefined)}
        {...modalInfo}
      />
      {product && (
        <EditProduct
          product={product}
          onClose={onEditClose}
          options={DYNAMIC_START_OF_WEEK_OPTIONS}
        />
      )}
    </Box>
  )
}
